import { Alert, Box, Button, Divider } from "@mui/material";
import deepEqual from "fast-deep-equal";
import React, { useCallback, use, useMemo, useRef, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { isDefined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../api/adminApi";
import type {
  BusinessCentralObjectType,
  Field,
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  GetRelatedObjectsResponse,
  LookupObjectReference,
  ObjectTypeToLookupReferences,
  PageLayout,
  RelatedObjectsUpdateRequests,
} from "../../api/types/objectTypes";
import usePageLeaveConfirmation from "../../hooks/usePageLeaveConfirmation";
import EntitySection from "./EntitySection";
import EntitySectionHeader from "./EntitySectionHeader";
import FieldValuesContainer from "./FieldValuesContainer";
import type { EmailFieldEditMode, FieldValuesState } from "./FieldValuesContext";
import { isAnyRequiredFieldEmpty } from "./fieldValuesManagement";
import { collectRelatedCitations, getFieldValueLabel, getLazyLookupOptionsType } from "./helpers";
import ObjectCardContext from "./ObjectCardContext";
import RelatedObjectsGroup from "./RelatedObjectsGroup";
import RelatedObjectsSummary from "./RelatedObjectsSummary";
import {
  areRelatedObjectRowsValid,
  getValidationErrorsByObjectType,
  getValidLookupOptionsOfRelatedObjects,
} from "./relatedObjectsValidation";
import type { RelatedObjectRowModel, RelatedObjectValuesState } from "./relatedObjectValuesState";
import {
  getInitialRelatedObjectValuesState,
  getRelatedObjectFieldsByObjectType,
  getRelatedObjectsUpdateRequests,
  initRowsAction,
} from "./relatedObjectValuesState";
import { getLinesDifference, type SummaryDifference } from "./objectCardPreviewState";
import { withReadOnlyFields } from "./objectCardSource";
import useObjectCardPreview from "./useObjectCardPreview";

const getObjectsAsLookupReferences = withErrorHandling(adminApi.getObjectsAsLookupReferences);

const hasUnsavedChanges = (
  fieldValues: FieldIdToValueMap,
  storedFieldValues: FieldIdToValueMap,
  relatedObjectValuesState: RelatedObjectValuesState
) => !deepEqual(fieldValues, storedFieldValues) || relatedObjectValuesState.dirtyObjectTypes.size > 0;

const getDifferenceRow = (
  summaryDifference: SummaryDifference | undefined,
  fields: Field[],
  fieldValues: FieldIdToValueMap,
  rows: RelatedObjectRowModel[]
) => {
  const field = fields.find((f) => f.id === summaryDifference?.amountFieldId);
  if (!summaryDifference || !field) {
    return undefined;
  }

  const lineAmounts = rows.map((row) => row[summaryDifference.lineAmountFieldId]);
  const value = getLinesDifference(fieldValues[field.id], lineAmounts);
  return value === undefined ? undefined : { label: summaryDifference.label, field, value };
};

interface Props {
  objectType: string;
  objectId: string;
  businessCentralType: BusinessCentralObjectType | undefined;
  fields: Field[];
  cardLayout: PageLayout;
  fieldValues: FieldIdToValueMap;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  lookupObjects?: FieldIdToLookupReferences;
  allowEditing: boolean;
  saveFieldValues: (fieldValues: FieldIdToValueMap, relatedObjects?: RelatedObjectsUpdateRequests) => Promise<boolean>;
  emailFieldEditMode?: EmailFieldEditMode;
  isLoading?: boolean;
}

const FieldValuesSection = ({
  objectType,
  objectId,
  businessCentralType,
  fields,
  cardLayout,
  fieldValues,
  fieldsConfidence,
  fieldsCitations,
  lookupObjects,
  allowEditing,
  emailFieldEditMode,
  saveFieldValues,
  isLoading,
}: Props) => {
  const objectCard = use(ObjectCardContext);
  const { sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const [fieldValuesState, setFieldValuesState] = useState<FieldValuesState>({
    fieldValues,
    isValid: true,
    fieldEditState: "viewing",
  });

  const [relatedObjectValuesState, setRelatedObjectValuesState] = useState(getInitialRelatedObjectValuesState);

  const setHasUnsavedEdits = objectCard?.setHasUnsavedEdits;

  const handleRelatedObjectValuesChange = useCallback(
    (action: React.SetStateAction<RelatedObjectValuesState>) => {
      setRelatedObjectValuesState(action);
      setHasUnsavedEdits?.(true);
    },
    [setHasUnsavedEdits]
  );

  const [lazyLookupOptions, setLazyLookupOptions] = useState<ObjectTypeToLookupReferences>({});
  const requestedLookupTypesRef = useRef(new Set<string>());

  const { setPromptOnPageLeave } = usePageLeaveConfirmation(false);

  const getLookupOptions = useCallback(
    () =>
      allowEditing && fields.some((f) => f.type === "Lookup") && !isLoading
        ? adminApi.getLookupOptions(objectType, objectId)
        : Promise.resolve(createApiResponse<ObjectTypeToLookupReferences>({})),
    [allowEditing, fields, objectType, objectId, isLoading]
  );

  const [lookupOptionsByObjectType, , { isFetching: isFetchingLookupOptions, setData: setLookupOptions }] =
    useFetch(getLookupOptions);

  const handleLookupOptionsNeeded = useCallback(
    async (lookupObjectTypes: string[]) => {
      const typesToLoad = [...new Set(lookupObjectTypes)].filter((type) => !requestedLookupTypesRef.current.has(type));
      typesToLoad.forEach((type) => requestedLookupTypesRef.current.add(type));

      await Promise.all(
        typesToLoad.map(async (type) => {
          const [references, error] = await getObjectsAsLookupReferences(type);
          if (error) {
            logError(error, "[FieldValuesSection] getObjectsAsLookupReferences");
            sendNotificationError("Failed to load options");
            requestedLookupTypesRef.current.delete(type);
            return;
          }

          setLazyLookupOptions((prev) => ({ ...prev, [type]: references }));
        })
      );
    },
    [sendNotificationError]
  );

  const getRelatedObjects = useCallback(() => {
    if (!objectId || !cardLayout.relatedObjectGrids?.length) {
      return Promise.resolve(createApiResponse<GetRelatedObjectsResponse>({ relatedObjectsByType: [] }));
    }

    return adminApi.getRelatedObjects(objectType, objectId, {
      relatedObjects: cardLayout.relatedObjectGrids.map((layout) => ({
        objectType: layout.objectType,
        fieldIds: layout.fieldIds,
      })),
      includeLookupOptions: allowEditing,
    });
  }, [allowEditing, cardLayout.relatedObjectGrids, objectId, objectType]);

  const [
    relatedObjectsResp,
    relatedObjectsError,
    { isFetching: isFetchingRelatedObjects, fetch: fetchRelatedObjects },
  ] = useFetch(getRelatedObjects, {
    onSetData: (data) => {
      setRelatedObjectValuesState(initRowsAction(data.relatedObjectsByType));
      setHasUnsavedEdits?.(
        hasUnsavedChanges(fieldValuesState.fieldValues, fieldValues, getInitialRelatedObjectValuesState())
      );
      objectCard?.setRelatedCitations(collectRelatedCitations(data.relatedObjectsByType));
    },
  });

  const lookupOptionsOfRelatedObjectsByObjectType = useMemo(
    () =>
      lookupOptionsByObjectType && relatedObjectsResp && allowEditing
        ? getValidLookupOptionsOfRelatedObjects(
            fieldValuesState.fieldValues,
            fields,
            lookupOptionsByObjectType,
            relatedObjectsResp.relatedObjectsByType.map((x) =>
              x.lookupOptions ? { ...x, lookupOptions: { ...x.lookupOptions, ...lazyLookupOptions } } : x
            )
          )
        : undefined,
    [
      allowEditing,
      fieldValuesState.fieldValues,
      fields,
      lookupOptionsByObjectType,
      relatedObjectsResp,
      lazyLookupOptions,
    ]
  );

  const source = objectCard?.source;
  const relatedObjectFieldsByObjectType = useMemo(() => {
    if (!cardLayout.relatedObjectGrids || !relatedObjectsResp) {
      return {};
    }

    const fieldsByObjectType = getRelatedObjectFieldsByObjectType(
      cardLayout.relatedObjectGrids,
      relatedObjectsResp.relatedObjectsByType
    );
    const fieldsWithSource = source && fieldsByObjectType[source.relatedObjectType];
    return fieldsWithSource
      ? {
          ...fieldsByObjectType,
          [source.relatedObjectType]: withReadOnlyFields(fieldsWithSource, source.readOnlyRelatedFieldIds),
        }
      : fieldsByObjectType;
  }, [cardLayout.relatedObjectGrids, relatedObjectsResp, source]);

  const handleRowDefaults = (fieldIds: string[]) => {
    const previewedFields = relatedObjectFieldsByObjectType[objectCard?.cardPreview?.relatedObjectType ?? ""] ?? [];
    const lazyOptionsTypes = previewedFields
      .filter((field) => fieldIds.includes(field.id))
      .map(getLazyLookupOptionsType);
    void handleLookupOptionsNeeded(lazyOptionsTypes.filter((type) => type !== undefined));
  };

  const handleFieldDefaults = (fieldDefaults: FieldIdToValueMap) => {
    setPromptOnPageLeave(true);
    setFieldValuesState((prev) => ({ ...prev, fieldValues: { ...prev.fieldValues, ...fieldDefaults } }));
    setHasUnsavedEdits?.(
      hasUnsavedChanges({ ...fieldValuesState.fieldValues, ...fieldDefaults }, fieldValues, relatedObjectValuesState)
    );
  };

  const preview = useObjectCardPreview({
    objectId,
    storedFieldValues: fieldValues,
    fieldValues: fieldValuesState.fieldValues,
    storedRelatedObjects: relatedObjectsResp?.relatedObjectsByType,
    relatedObjectValuesState,
    setRelatedObjectValuesState,
    onFieldDefaults: handleFieldDefaults,
    onRowDefaults: handleRowDefaults,
  });

  const validationErrorsByObjectType = useMemo(
    () =>
      allowEditing
        ? getValidationErrorsByObjectType(
            relatedObjectValuesState.rowsByObjectType,
            relatedObjectFieldsByObjectType,
            lookupOptionsOfRelatedObjectsByObjectType
          )
        : {},
    [
      allowEditing,
      lookupOptionsOfRelatedObjectsByObjectType,
      relatedObjectFieldsByObjectType,
      relatedObjectValuesState.rowsByObjectType,
    ]
  );

  const mergeFieldValuesState = (stateUpdate: Partial<FieldValuesState>) => {
    setPromptOnPageLeave(true);
    setFieldValuesState((prev) => ({ ...prev, ...stateUpdate }));
    setHasUnsavedEdits?.(
      hasUnsavedChanges(stateUpdate.fieldValues ?? fieldValuesState.fieldValues, fieldValues, relatedObjectValuesState)
    );
  };

  const handleFieldValuesStateChange = (stateUpdate: Partial<FieldValuesState>) => {
    if (stateUpdate.fieldValues && fieldValuesState.fieldEditState !== "canceling") {
      preview.reportUserChange(undefined, fieldValuesState.fieldValues, stateUpdate.fieldValues, (fieldId, value) =>
        getFieldValueLabel(fields, fieldId, value, lookupOptionsByObjectType ?? {}, lookupObjects?.[fieldId] ?? [])
      );
    }

    mergeFieldValuesState(stateUpdate);
  };

  const handleAddLookupOption = useCallback(
    (objectType: string, lookupObjectReference: LookupObjectReference) => {
      setLookupOptions((prev) => {
        if (!prev) {
          return { [objectType]: [lookupObjectReference] };
        }

        const updatedOptions: ObjectTypeToLookupReferences = { ...prev };
        if (!updatedOptions[objectType]) {
          updatedOptions[objectType] = [];
        }

        if (updatedOptions[objectType].some((option) => option.objectId === lookupObjectReference.objectId)) {
          return prev;
        }

        updatedOptions[objectType].push(lookupObjectReference);

        return updatedOptions;
      });
    },
    [setLookupOptions]
  );

  const handleSaveClick = async () => {
    const changedFieldValues = Object.entries(fieldValuesState.fieldValues).reduce<FieldIdToValueMap>(
      (result, [fieldId, value]) => {
        if (
          !deepEqual(value, fieldValues[fieldId]) &&
          fields.some((f) => f.id === fieldId && !f.attributes.includes("ReadOnly"))
        ) {
          result[fieldId] = value;
        }
        return result;
      },
      {}
    );

    const relatedObjectsUpdateRequests = getRelatedObjectsUpdateRequests(
      relatedObjectValuesState,
      cardLayout.relatedObjectGrids ?? [],
      objectId
    );

    setIsSaving(true);
    const isSaved = await saveFieldValues(changedFieldValues, relatedObjectsUpdateRequests);
    setIsSaving(false);

    if (isSaved && relatedObjectsUpdateRequests) {
      fetchRelatedObjects();
    }

    if (isSaved) {
      setHasUnsavedEdits?.(false);
    }

    setPromptOnPageLeave(!isSaved);
  };

  const handleCancelClick = () => {
    if (fieldValuesState.fieldEditState === "editing") {
      mergeFieldValuesState({ isValid: true, fieldEditState: "canceling" });
    } else {
      mergeFieldValuesState({
        fieldValues: structuredClone(fieldValues),
        isValid: true,
        fieldEditState: "viewing",
      });
    }

    setRelatedObjectValuesState(initRowsAction(relatedObjectsResp?.relatedObjectsByType ?? []));
    preview.reset();

    setPromptOnPageLeave(false);
    setHasUnsavedEdits?.(false);
  };

  const hasChanges = hasUnsavedChanges(fieldValuesState.fieldValues, fieldValues, relatedObjectValuesState);

  // Need to check this additionally since validation state is not updated until the field is touched
  const areThereEmptyRequiredFields = isAnyRequiredFieldEmpty(fields, fieldValuesState.fieldValues);

  const isStateValid =
    fieldValuesState.isValid && !areThereEmptyRequiredFields && areRelatedObjectRowsValid(validationErrorsByObjectType);

  const disableButtons = fieldValuesState.fieldEditState === "editing" || isSaving || !hasChanges;
  const disableSaveButton = disableButtons || !isStateValid || preview.isWaitingForDefaults;

  const loading = isLoading || isFetchingLookupOptions || isFetchingRelatedObjects || isSaving;

  return (
    <EntitySection isLoading={loading}>
      <EntitySectionHeader title="Details">
        {allowEditing && (
          <>
            <Button disabled={disableButtons} onClick={handleCancelClick} variant="text" color="secondary">
              Cancel
            </Button>
            <Button
              loading={preview.isWaitingForDefaults}
              disabled={disableSaveButton}
              onClick={handleSaveClick}
              variant="contained"
              color="primary"
            >
              Save Changes
            </Button>
          </>
        )}
      </EntitySectionHeader>
      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, pb: 2 }}>
        <FieldValuesContainer
          objectType={objectType}
          businessCentralType={businessCentralType}
          fields={fields}
          cardLayout={cardLayout}
          initialFieldValues={fieldValues}
          lookupObjects={lookupObjects}
          lookupOptionsByObjectType={lookupOptionsByObjectType}
          onAddLookupOption={handleAddLookupOption}
          fieldsConfidence={fieldsConfidence}
          fieldsCitations={fieldsCitations}
          allowEditing={allowEditing}
          fieldValuesState={fieldValuesState}
          onFieldValuesStateChange={handleFieldValuesStateChange}
          emailFieldEditMode={emailFieldEditMode}
          fieldsAutoFillTooltips={preview.fieldsAutoFillTooltips}
        />

        {relatedObjectsError && (
          <>
            <Divider sx={{ my: 3 }} />
            <DataLoadingFailed
              title="Failed to load related objects"
              text={cardLayout.relatedObjectGrids?.map((x) => x.name).join(", ") ?? ""}
            />
          </>
        )}

        {!relatedObjectsError && relatedObjectsResp === undefined && <InlineLoader />}

        {relatedObjectsResp !== undefined &&
          Object.entries(relatedObjectValuesState.rowsByObjectType).map(([relatedObjectType, rows]) => {
            const layout = cardLayout.relatedObjectGrids?.find((x) => x.objectType === relatedObjectType);
            if (!layout) {
              return null;
            }

            const relatedObjectsData = relatedObjectsResp.relatedObjectsByType.find(
              (x) => x.objectType === relatedObjectType
            );
            if (!relatedObjectsData) {
              return null;
            }

            const lookupOptions =
              (lookupOptionsOfRelatedObjectsByObjectType
                ? lookupOptionsOfRelatedObjectsByObjectType[relatedObjectType]
                : undefined) ?? {};

            const validationErrors = validationErrorsByObjectType[relatedObjectType] ?? {};

            const summaryFields = layout.parentSummaryFieldIds
              .map((id) => fields.find((f) => f.id === id))
              .filter(isDefined());
            const summaryValues = { ...fieldValuesState.fieldValues, ...preview.fieldValues };
            const hasSummaryValues = summaryFields.some((f) => summaryValues[f.id] != null);
            const isPreviewedType = relatedObjectType === preview.config?.relatedObjectType;
            const difference = getDifferenceRow(
              isPreviewedType ? preview.config?.summaryDifference : undefined,
              fields,
              fieldValuesState.fieldValues,
              rows
            );

            return (
              <React.Fragment key={relatedObjectType}>
                <Divider sx={{ my: 2 }} />
                <RelatedObjectsGroup
                  layout={layout}
                  fields={relatedObjectFieldsByObjectType[relatedObjectType] ?? []}
                  objectPermissions={relatedObjectsData.objectPermissions}
                  rows={rows}
                  setState={handleRelatedObjectValuesChange}
                  allowEditing={allowEditing}
                  allowAddingAndDeletingRows={relatedObjectType !== source?.relatedObjectType}
                  loading={isFetchingRelatedObjects}
                  lookupOptions={lookupOptions}
                  onLookupOptionsNeeded={handleLookupOptionsNeeded}
                  validationErrors={validationErrors}
                  cellDecorations={isPreviewedType ? preview.cellDecorations : undefined}
                  columnLayouts={isPreviewedType ? preview.config?.relatedColumnLayouts : undefined}
                  noOptionsTexts={isPreviewedType ? preview.relatedNoOptionsTexts : undefined}
                  onUserRowChange={isPreviewedType ? preview.reportUserChange : undefined}
                >
                  {rows.length > 0 && (hasSummaryValues || difference) && (
                    <RelatedObjectsSummary
                      fields={hasSummaryValues ? summaryFields : []}
                      fieldValues={summaryValues}
                      difference={difference}
                    />
                  )}
                </RelatedObjectsGroup>
                {isPreviewedType && preview.hasFailed && (
                  <Alert severity="warning">
                    Couldn&apos;t check the changes. Values that fill in automatically may be missing — review them
                    before saving.
                  </Alert>
                )}
              </React.Fragment>
            );
          })}
      </Box>
    </EntitySection>
  );
};

export default FieldValuesSection;
